import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useEffect, useCallback } from "react";
const FADE_MS = 150;
const PRIORITY_RANGE = 3; // preload current frame +/- 3 (7 frames total)
const BG_CONCURRENCY = 3; // background loading concurrency limit
const ImageRotator = ({ images, height = 240, controlledIndex, onIndexChange, disableDrag = false, fallbackSrc, onAllFramesFailed }) => {
    const [internalIndex, setInternalIndex] = useState(0);
    const [displayedSrc, setDisplayedSrc] = useState(images[0] || "");
    const [prevSrc, setPrevSrc] = useState("");
    const [fading, setFading] = useState(false);
    const [priorityLoaded, setPriorityLoaded] = useState(false);
    const [allFailed, setAllFailed] = useState(false);
    const fadeTimer = useRef(null);
    const isDragging = useRef(false);
    const loadedFrames = useRef(new Set());
    const lastX = useRef(0);
    const velocityX = useRef(0);
    const lastTimestamp = useRef(0);
    const accumulatedDelta = useRef(0);
    const momentumRafId = useRef(null);
    const loadedCount = useRef(0);
    const failedCount = useRef(0);
    const frameCount = images.length;
    const index = controlledIndex !== undefined ? controlledIndex : internalIndex;
    const updateIndex = useCallback((newIndex) => {
        const normalized = ((newIndex % frameCount) + frameCount) % frameCount;
        if (controlledIndex !== undefined) {
            onIndexChange === null || onIndexChange === void 0 ? void 0 : onIndexChange(normalized);
        }
        else {
            setInternalIndex(normalized);
        }
    }, [controlledIndex, frameCount, onIndexChange]);
    // Momentum scroll after pointer release
    const applyMomentum = useCallback(() => {
        if (Math.abs(velocityX.current) < 0.5) {
            momentumRafId.current = null;
            return;
        }
        accumulatedDelta.current += velocityX.current;
        if (Math.abs(accumulatedDelta.current) >= 10) {
            const steps = Math.floor(Math.abs(accumulatedDelta.current) / 10);
            const direction = accumulatedDelta.current > 0 ? 1 : -1;
            updateIndex(index + direction * steps);
            accumulatedDelta.current = accumulatedDelta.current % 10;
        }
        velocityX.current *= 0.88; // friction damping
        momentumRafId.current = requestAnimationFrame(applyMomentum);
    }, [index, updateIndex]);
    useEffect(() => {
        return () => {
            if (momentumRafId.current)
                cancelAnimationFrame(momentumRafId.current);
            if (fadeTimer.current)
                clearTimeout(fadeTimer.current);
        };
    }, []);
    useEffect(() => {
        if (!images.length) {
            setDisplayedSrc("");
            return;
        }
        if (!displayedSrc || !images.includes(displayedSrc)) {
            setDisplayedSrc(images[Math.min(index, images.length - 1)] || images[0] || "");
        }
    }, [images, index, displayedSrc]);
    const onPointerDown = (event) => {
        event.preventDefault();
        if (momentumRafId.current) {
            cancelAnimationFrame(momentumRafId.current);
            momentumRafId.current = null;
        }
        isDragging.current = true;
        lastX.current = event.clientX;
        velocityX.current = 0;
        accumulatedDelta.current = 0;
        lastTimestamp.current = event.timeStamp;
        event.currentTarget.setPointerCapture(event.pointerId);
    };
    const onPointerMove = (event) => {
        if (!isDragging.current)
            return;
        const delta = event.clientX - lastX.current;
        const dt = event.timeStamp - lastTimestamp.current;
        if (dt > 0) {
            velocityX.current = delta / dt * 16; // scale to ~60fps frame
        }
        accumulatedDelta.current += delta;
        lastX.current = event.clientX;
        lastTimestamp.current = event.timeStamp;
        if (Math.abs(accumulatedDelta.current) >= 8) {
            const steps = Math.floor(Math.abs(accumulatedDelta.current) / 8);
            const direction = accumulatedDelta.current > 0 ? 1 : -1;
            updateIndex(index + direction * steps);
            accumulatedDelta.current = accumulatedDelta.current % 8;
        }
    };
    const onPointerUp = () => {
        if (!isDragging.current)
            return;
        isDragging.current = false;
        if (Math.abs(velocityX.current) > 1) {
            momentumRafId.current = requestAnimationFrame(applyMomentum);
        }
    };
    // Progressive image loading: priority frames first, then background with throttled concurrency
    useEffect(() => {
        if (!images.length)
            return;
        let cancelled = false;
        loadedFrames.current = new Set();
        setPriorityLoaded(false);
        const loadImage = (src) => new Promise((resolve) => {
            if (!src || loadedFrames.current.has(src)) {
                resolve();
                return;
            }
            const img = new Image();
            img.onload = () => { loadedFrames.current.add(src); resolve(); };
            img.onerror = () => { resolve(); };
            img.src = src;
        });
        // 1. Priority: load current frame +/- PRIORITY_RANGE (7 frames) immediately
        const priorityIndices = [];
        for (let offset = -PRIORITY_RANGE; offset <= PRIORITY_RANGE; offset++) {
            const i = ((index + offset) % images.length + images.length) % images.length;
            if (!priorityIndices.includes(i))
                priorityIndices.push(i);
        }
        const prioritySrcs = priorityIndices.map((i) => images[i]).filter(Boolean);
        Promise.all(prioritySrcs.map(loadImage)).then(() => {
            if (!cancelled)
                setPriorityLoaded(true);
        });
        // 2. Background: load remaining frames with concurrency limit
        const remainingSrcs = images.filter((src) => src && !prioritySrcs.includes(src));
        const loadWithConcurrency = async (srcs, limit) => {
            let i = 0;
            const next = async () => {
                if (cancelled || i >= srcs.length)
                    return;
                const src = srcs[i++];
                await loadImage(src);
                return next();
            };
            const workers = Array.from({ length: Math.min(limit, srcs.length) }, () => next());
            await Promise.all(workers);
        };
        // Start background loading after a short delay to let priority frames finish first
        const bgTimer = setTimeout(() => {
            if (!cancelled)
                loadWithConcurrency(remainingSrcs, BG_CONCURRENCY);
        }, 100);
        return () => {
            cancelled = true;
            clearTimeout(bgTimer);
        };
    }, [images]);
    useEffect(() => {
        const nextSrc = images[index] || "";
        if (!nextSrc || nextSrc === displayedSrc)
            return;
        let cancelled = false;
        const nextImage = new Image();
        nextImage.src = nextSrc;
        const commitFrame = () => {
            if (cancelled)
                return;
            if (fadeTimer.current)
                clearTimeout(fadeTimer.current);
            setPrevSrc(displayedSrc);
            setDisplayedSrc(nextSrc);
            setFading(true);
            requestAnimationFrame(() => {
                setFading(false);
                fadeTimer.current = setTimeout(() => {
                    setPrevSrc("");
                    fadeTimer.current = null;
                }, FADE_MS);
            });
        };
        if (nextImage.complete) {
            commitFrame();
        }
        else {
            nextImage.onload = commitFrame;
        }
        return () => {
            cancelled = true;
            nextImage.onload = null;
        };
    }, [images, index, displayedSrc]);
    // When all frames failed, show fallback or empty container
    if (allFailed) {
        if (fallbackSrc) {
            return (_jsx("div", { style: {
                    width: "100%",
                    height: typeof height === "number" ? `${height}px` : height,
                    background: "#f8f8f8",
                    borderRadius: "8px",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }, children: _jsx("img", { src: fallbackSrc, alt: "Vehicle image", style: {
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        pointerEvents: "none",
                    }, loading: "eager", draggable: false }) }));
        }
        return (_jsx("div", { style: {
                width: "100%",
                height: typeof height === "number" ? `${height}px` : height,
                background: "#f8f8f8",
                borderRadius: "8px",
            } }));
    }
    return (_jsxs("div", { ...(!disableDrag ? {
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerLeave: onPointerUp,
        } : {}), style: {
            width: "100%",
            height: typeof height === "number" ? `${height}px` : height,
            background: "transparent",
            borderRadius: "8px",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            touchAction: "pan-y",
            cursor: disableDrag ? "default" : (isDragging.current ? "grabbing" : "grab"),
            userSelect: "none",
            position: "relative",
        }, children: [!priorityLoaded && (_jsx("div", { style: {
                    position: "absolute",
                    inset: 0,
                    zIndex: 2,
                    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite",
                    borderRadius: "8px",
                } })), _jsx("img", { src: displayedSrc || images[index], alt: `360° view frame ${index + 1}`, style: {
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    pointerEvents: "none",
                }, loading: "eager", draggable: false, onDragStart: (e) => e.preventDefault() }), prevSrc && (_jsx("img", { src: prevSrc, alt: "", style: {
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    pointerEvents: "none",
                    opacity: fading ? 1 : 0,
                    transition: `opacity ${FADE_MS}ms ease-out`,
                }, draggable: false })), _jsx("style", { children: `@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }` })] }));
};
export default ImageRotator;
